import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BillingBanner from './billing/BillingBanner';
import { useAppContext } from '../context/AppContext';
import { isGlobalCatalogAdmin } from '../utils/catalogAdmin';
import { Task } from 'shared/types';
import TaskMemberModal from './TaskMemberModal';
import AntMascot from './AntMascot';
import { i18n } from '../i18n/i18n';
import { useLanguage } from '../context/LanguageContext';
import { KidButton, KidCard, KidProgress, KidSectionHeading } from './kid/KidUI';
import { KidTaskCard } from './kid/KidTaskCard';
import { Star } from './kid/KidIcon';
import { pickFamilyGoal, pickStarGoal, pluralKey } from '../utils/starGoal';
import { getRoom, getRoomName } from '../utils/roomName';
import ParentHome from './parent/ParentHome';
import { isAvailableNow } from '../utils/taskAvailability';
import { showToast } from './kid/Toast';

/** How many tasks anyone can take are shown on Home; the rest are on the Tasks page. */
export const HOME_TAKE_LIMIT = 3;

/**
 * Home (kid-first redesign step 4 — mock-ups phone-01 / tablet-*-01).
 * Star goal on top (D4, automatic), "My tasks" with one "Let's go" each (soonest due first), a
 * "waiting for a grown-up" strip for finished tasks, "Want more stars?" with tasks anyone can take, and the family goal where the sibling leaderboard was (D5;
 * ranking now only lives in Stats → Family, governed by the stats visibility setting).
 * Tablet landscape / computer: tasks on the left, goals on the right.
 */

function parseTaskDueMs(task: any): number | null {
  const raw = task?.due_date;
  if (raw == null || raw === '') return null;
  const ms = new Date(raw).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function startOfLocalDayMs(refMs: number): number {
  const d = new Date(refMs);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Overdue first, then by due date ascending; tasks without due date last. */
function compareMyTasksDashboard(a: any, b: any): number {
  const sod = startOfLocalDayMs(Date.now());
  const aDue = parseTaskDueMs(a);
  const bDue = parseTaskDueMs(b);
  const aOver = aDue != null && aDue < sod;
  const bOver = bDue != null && bDue < sod;
  if (aOver !== bOver) return aOver ? -1 : 1;
  if (aDue == null && bDue == null) return 0;
  if (aDue == null) return 1;
  if (bDue == null) return -1;
  return aDue - bDue;
}

const Dashboard: React.FC = () => {
  const { current_user, current_family, refreshFamilyData, claimTask } = useAppContext();
  const { current_language } = useLanguage();
  const navigate = useNavigate();
  const [taskModalTask, setTaskModalTask] = useState<Task | null>(null);
  const [claimingId, setClaimingId] = useState<string | null>(null);

  if (!current_family || !current_user) {
    return (
      <div className="p-8 text-center">
        <h3 className="text-xl font-semibold text-kid-text dark:text-kid-text-dark">{i18n.t('common.loading')}</h3>
      </div>
    );
  }

  // Grown-ups get their own Home: approvals, the family, and shortcuts (redesign step 6).
  if ((current_user as any).is_admin) return <ParentHome />;

  const me = current_family.members.find((m: any) => m.id === current_user.id);
  const myStars = Number(me?.points ?? current_user.points ?? 0);

  const myTasks = current_family.tasks
    .filter((t: any) => t.assigned_to === current_user.id && !t.is_completed && isAvailableNow(t))
    .sort(compareMyTasksDashboard);
  const waiting = current_family.tasks.filter(
    (t: any) => t.is_completed && !t.verified && t.completed_by === current_user.id,
  );
  // Tasks anyone can take, always on Home so children find them and can earn more stars when they
  // feel like it (Jens 2026-10-13). Soonest deadline first.
  const canTake = current_family.tasks
    .filter((t: any) => !t.is_completed && !t.assigned_to && isAvailableNow(t))
    .sort(compareMyTasksDashboard);

  const handleTake = async (task: any) => {
    if (claimingId) return;
    setClaimingId(task.id);
    try {
      await claimTask(task.id);
      showToast({ message: i18n.t('task_board.moved_to_yours').replace('{{task}}', task.title) });
      document.getElementById('home-my-tasks')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error: any) {
      showToast({ message: error?.message || i18n.t('dashboard.take_failed'), tone: 'error' });
    } finally {
      setClaimingId(null);
    }
  };

  const goal = pickStarGoal(current_family.rewards, current_user.id);
  const familyGoal = pickFamilyGoal(current_family.rewards);

  // ── pieces ──
  const taskCard = (task: any) => (
    <KidTaskCard
      key={task.id}
      task={task}
      roomName={getRoomName(current_family.rooms, task.room_id)}
      roomImage={getRoom(current_family.rooms, task.room_id)?.image_url}
      actionLabel={i18n.t('task_flow.start_task')}
      actionTrailing={<span aria-hidden>→</span>}
      onAction={() => setTaskModalTask(task as Task)}
    />
  );

  const waitingStrip = (task: any) => (
    <KidCard key={task.id} tone="peach" className="flex items-center gap-4 !py-4">
      <span className="text-2xl" aria-hidden>⌛</span>
      <div className="min-w-0">
        <p className="font-bold text-lg leading-tight truncate">{task.title}</p>
        <p className="text-base">{i18n.t(pluralKey('dashboard.waiting', task.points)).replace('{{n}}', String(task.points))}</p>
      </div>
    </KidCard>
  );

  const starGoalCard = (
    <KidCard tone="mint" className="flex items-center gap-4 lg:flex-col lg:text-center">
      <AntMascot pose="celebrate" className="w-20 h-20 lg:w-32 lg:h-32 shrink-0" />
      <div className="flex-1 min-w-0 w-full">
        {goal ? (
          <>
            <p className="font-kid-heading font-extrabold text-xl md:text-2xl leading-tight mb-3">
              {goal.ready
                ? i18n.t('notifications.approved_reward_ready').replace('{{reward}}', goal.reward.title)
                : i18n.t('notifications.approved_more_to_goal').replace('{{n}}', String(goal.remaining)).replace('{{reward}}', goal.reward.title)}
            </p>
            <KidProgress value={Math.min(goal.have, goal.need)} max={goal.need} size="md" label={goal.reward.title} />
            <p className="mt-2 text-base font-bold">
              {i18n.t('dashboard.goal_of').replace('{{have}}', String(Math.min(goal.have, goal.need))).replace('{{need}}', String(goal.need))}
            </p>
            {goal.ready && (
              <KidButton size="md" className="mt-3" onClick={() => navigate('/rewards')} trailing={<span aria-hidden>→</span>}>
                {i18n.t('dashboard.goal_get_it')}
              </KidButton>
            )}
          </>
        ) : (
          <p className="font-bold text-lg">{i18n.t('dashboard.goal_none')}</p>
        )}
      </div>
    </KidCard>
  );

  const familyGoalCard = familyGoal && (
    <KidCard tone="panel">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h2 className="font-kid-heading font-extrabold text-xl md:text-2xl">{i18n.t('dashboard.family_goal')}</h2>
        <span className="font-bold text-base whitespace-nowrap">
          {i18n.t('dashboard.goal_of').replace('{{have}}', String(Math.min(familyGoal.have, familyGoal.need))).replace('{{need}}', String(familyGoal.need))}
        </span>
      </div>
      <KidProgress value={Math.min(familyGoal.have, familyGoal.need)} max={familyGoal.need} color="family" size="md" label={familyGoal.reward.title} />
      <p className="mt-3 text-base font-bold">
        {familyGoal.ready
          ? i18n.t('dashboard.family_goal_ready').replace('{{reward}}', familyGoal.reward.title)
          : i18n.t('dashboard.family_goal_more').replace('{{n}}', String(familyGoal.remaining)).replace('{{reward}}', familyGoal.reward.title)}
      </p>
    </KidCard>
  );

  return (
    <div className="w-full max-w-[1180px] mx-auto px-4 md:px-8 py-4 md:py-7 text-kid-text dark:text-kid-text-dark" lang={current_language}>
      <BillingBanner />
      {isGlobalCatalogAdmin(current_user) && (
        <KidCard tone="peach" className="mb-5 flex flex-wrap items-center justify-between gap-3 !py-3">
          <span className="text-base font-bold">Master admin: standard tools and standard tasks for all families.</span>
          <Link to="/global-admin/tools" className="min-h-[44px] inline-flex items-center rounded-full px-5 bg-kid-card dark:bg-kid-card-dark border-2 border-kid-border-strong font-bold">
            Open master admin
          </Link>
        </KidCard>
      )}

      {/* greeting + star total */}
      <div className="flex items-center justify-between gap-3 mb-5">
        <h1 className="font-kid-heading font-extrabold text-3xl md:text-4xl leading-tight">
          {i18n.t('dashboard.hi_name').replace('{{name}}', current_user.name)}
        </h1>
        <span className="shrink-0 inline-flex items-center gap-2 rounded-full bg-kid-peach dark:bg-amber-900/50 px-4 min-h-[48px] font-kid-heading font-extrabold text-2xl">
          <Star size={26} /> {myStars}
        </span>
      </div>

      {/* phone: one column in the mock-up's order; ≥1024 px: tasks left, goals right */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_400px] lg:gap-8 lg:items-start">
        <div className="order-1 lg:col-start-2 lg:row-start-1">{starGoalCard}</div>

        <section className="order-2 lg:col-start-1 lg:row-start-1 lg:row-span-2 flex flex-col gap-4">
          <div id="home-my-tasks" className="scroll-mt-24">
            <KidSectionHeading>{i18n.t('dashboard.my_tasks_primary_title')}</KidSectionHeading>
          </div>
          {myTasks.length === 0 ? (
            <KidCard tone="mint" className="flex items-center gap-4">
              <AntMascot pose="wave" className="w-16 h-16 shrink-0" />
              <p className="font-bold text-lg">{i18n.t('dashboard.no_tasks_now')}</p>
            </KidCard>
          ) : (
            myTasks.map(taskCard)
          )}
          {waiting.map(waitingStrip)}

          {canTake.length > 0 && (
            <section aria-labelledby="home-can-take" className="flex flex-col gap-4 mt-3">
              <KidCard tone="peach" className="flex items-center gap-4 !py-4">
                <AntMascot pose="celebrate" className="w-14 h-14 shrink-0" />
                <div className="min-w-0">
                  <h2 id="home-can-take" className="font-kid-heading font-extrabold text-xl md:text-2xl leading-tight">
                    {i18n.t('dashboard.more_stars_title')}
                  </h2>
                  <p className="text-base">{i18n.t('dashboard.more_stars_hint')}</p>
                </div>
              </KidCard>
              {canTake.slice(0, HOME_TAKE_LIMIT).map((task: any) => (
                <KidTaskCard
                  key={task.id}
                  task={task}
                  roomName={getRoomName(current_family.rooms, task.room_id)}
                  roomImage={getRoom(current_family.rooms, task.room_id)?.image_url}
                  actionLabel={i18n.t('task_board.take_task')}
                  actionVariant="secondary"
                  busy={claimingId === task.id}
                  onAction={() => handleTake(task)}
                />
              ))}
              {canTake.length > HOME_TAKE_LIMIT && (
                <Link to="/tasks" className="self-center inline-flex items-center gap-1 min-h-[44px] px-3 font-bold underline text-kid-green-shadow dark:text-green-300">
                  {i18n.t('dashboard.see_all_can_take').replace('{{n}}', String(canTake.length))} <span aria-hidden>→</span>
                </Link>
              )}
            </section>
          )}
        </section>

        {familyGoalCard && <div className="order-3 lg:col-start-2 lg:row-start-2">{familyGoalCard}</div>}
      </div>

      <TaskMemberModal
        task={taskModalTask}
        open={!!taskModalTask}
        startInFlow
        onClose={() => {
          setTaskModalTask(null);
          void refreshFamilyData();
        }}
      />
    </div>
  );
};

export default Dashboard;
